import { apiRequest, type components } from './core';

/**
 * Students API endpoints
 */

// Request types from auto-generated OpenAPI schemas
export type StudentCreateData = components['schemas']['CreateStudentRequest'];
export type StudentUpdateData = components['schemas']['UpdateStudentRequest'];

// Response type from auto-generated OpenAPI schemas
export type Student = components['schemas']['StudentResponse'];

// Bulk import types
export type BulkStudentRow = components['schemas']['BulkStudentRow'];
export type BulkStudentsValidationResponse = components['schemas']['BulkStudentsValidationResponse'];
export type BulkStudentsCreateResponse = components['schemas']['BulkStudentsCreateResponse'];

// Grade Progression types from auto-generated OpenAPI schemas
export type GradeProgressionOptionsData = components['schemas']['GradeProgressionOptionsRequest'];
export type GradeProgressionPreviewResponse = components['schemas']['GradeProgressionPreviewResponse'];
export type GradeProgressionResultResponse = components['schemas']['GradeProgressionResultResponse'];

export type PaginatedStudentsResult = {
  success: boolean;
  data: Student[];
  total: number;
  limit: number;
  offset: number;
  availableGrades: number[];
};

export const studentsApi = {
  /**
   * Get all students (no pagination — backward compat for BulkQRCodes, etc.)
   */
  getAll: async () => {
    return apiRequest<{
      success: boolean;
      data: Student[];
    }>('/api/students');
  },

  /**
   * Get students with server-side pagination, filtering, and sorting
   */
  getPaginated: async (params: {
    search?: string;
    gradeLevel?: number;
    enrollmentStatus?: string;
    includeInactive?: boolean;
    limit: number;
    offset: number;
    sortBy?: string;
    sortOrder?: string;
  }) => {
    const query = new URLSearchParams();
    query.set('limit', params.limit.toString());
    query.set('offset', params.offset.toString());
    if (params.search) query.set('search', params.search);
    if (params.gradeLevel !== undefined) query.set('gradeLevel', params.gradeLevel.toString());
    if (params.enrollmentStatus && params.enrollmentStatus !== 'all') query.set('enrollmentStatus', params.enrollmentStatus);
    if (params.includeInactive !== undefined) query.set('includeInactive', params.includeInactive.toString());
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortOrder) query.set('sortOrder', params.sortOrder);

    return apiRequest<PaginatedStudentsResult>(`/api/students?${query.toString()}`);
  },

  /**
   * Search students with client-side filtering
   * Note: Backend returns all students; filtering is done client-side
   */
  search: async (params: { search?: string; limit?: number; offset?: number }) => {
    // Get all students from backend
    const response = await apiRequest<{
      success: boolean;
      data: Student[];
    }>('/api/students');

    let students = response.data;

    // Client-side search filtering
    if (params.search) {
      const searchLower = params.search.toLowerCase();
      students = students.filter(
        (s) =>
          s.firstName.toLowerCase().includes(searchLower) ||
          s.lastName.toLowerCase().includes(searchLower) ||
          (s.studentId && s.studentId.toLowerCase().includes(searchLower))
      );
    }

    const total = students.length;

    // Apply pagination
    const offset = params.offset || 0;
    const limit = params.limit || students.length;
    students = students.slice(offset, offset + limit);

    return {
      success: true,
      data: {
        students,
        total,
      },
    };
  },

  /**
   * Get student by ID
   */
  getById: async (id: string) => {
    return apiRequest<{
      success: boolean;
      data: Student;
    }>(`/api/students/${id}`);
  },

  /**
   * Create new student
   */
  create: async (data: StudentCreateData) => {
    return apiRequest<{
      success: boolean;
      data: Student;
    }>('/api/students', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update student
   */
  update: async (id: string, data: StudentUpdateData) => {
    return apiRequest<{
      success: boolean;
      data: Student;
    }>(`/api/students/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete student (hard delete - only for students with no records)
   */
  delete: async (id: string) => {
    return apiRequest<{
      success: boolean;
      message: string;
    }>(`/api/students/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Deactivate student (soft delete - sets active to false)
   */
  deactivate: async (id: string) => {
    return apiRequest<{
      success: boolean;
      message: string;
      data: Student;
    }>(`/api/students/${id}/deactivate`, {
      method: 'PATCH',
    });
  },

  /**
   * Reactivate student (sets active to true)
   */
  reactivate: async (id: string) => {
    return apiRequest<{
      success: boolean;
      message: string;
      data: Student;
    }>(`/api/students/${id}/reactivate`, {
      method: 'PATCH',
    });
  },

  /**
   * Check student dependencies for smart action dialog
   * Returns what actions are possible and why
   */
  checkDependencies: async (id: string) => {
    return apiRequest<{
      success: boolean;
      data: {
        canDeactivate: boolean;
        canDelete: boolean;
        dependencies: {
          enrollments: number;
          behaviorRecords: number;
          attendanceRecords: number;
          blockOverrides: number;
        };
        blockedBy: string[];
      };
    }>(`/api/students/${id}/dependencies`, {
      method: 'GET',
    });
  },

  /**
   * Bulk import students - validate only (dryRun: true)
   */
  bulkValidate: async (rows: BulkStudentRow[]) => {
    return apiRequest<{
      success: boolean;
      data: BulkStudentsValidationResponse;
    }>('/api/students/bulk', {
      method: 'POST',
      body: JSON.stringify({ rows, dryRun: true }),
    });
  },

  /**
   * Bulk import students - validate and create (dryRun: false)
   */
  bulkCreate: async (rows: BulkStudentRow[]) => {
    return apiRequest<{
      success: boolean;
      data: BulkStudentsCreateResponse;
    }>('/api/students/bulk', {
      method: 'POST',
      body: JSON.stringify({ rows, dryRun: false }),
    });
  },

  // ============================================
  // Grade Progression API
  // ============================================

  /**
   * Get preview of grade progression
   * Shows students grouped by grade with projected new grades
   */
  getGradeProgressionPreview: async (params?: { schoolYearId?: string; maxGradeLevel?: number }) => {
    const query = new URLSearchParams();
    if (params?.schoolYearId) query.set('schoolYearId', params.schoolYearId);
    if (params?.maxGradeLevel !== undefined) query.set('maxGradeLevel', String(params.maxGradeLevel));
    const suffix = query.toString() ? `?${query.toString()}` : '';

    return apiRequest<{
      success: boolean;
      data: GradeProgressionPreviewResponse;
    }>(`/api/students/grade-progression-preview${suffix}`);
  },

  /**
   * Execute grade progression for selected students
   * Advances grades by 1, graduates max-grade students
   */
  advanceGrades: async (data: GradeProgressionOptionsData) => {
    return apiRequest<{
      success: boolean;
      data: GradeProgressionResultResponse;
    }>('/api/students/advance-grades', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

/**
 * Enrollment Types
 */
/**
 * Student enrollment record for class rosters
 * Extends base Student type with enrollment timestamps
 */
export interface ClassRosterStudent extends Pick<
  Student,
  'id' | 'firstName' | 'lastName' | 'gradeLevel' | 'photoUrl' | 'photoFileId' | 'parentLinkCode'
> {
  studentId: string; // Required in enrollment context (Student.studentId is string | null)
  enrolledAt: string | null;
  droppedAt: string | null;
}

export interface EnrolledStudentWithBlocks extends ClassRosterStudent {
  blocks: Array<{
    blockId: string;
    blockName: string;
    displayOrder: number;
    isEnrolled: boolean;
    overrideReason: string | null;
  }>;
}

/**
 * Class enrollment record for student schedules
 * Extends base Class type with enrollment timestamps
 */
export interface StudentEnrollmentClass {
  id: string;
  name: string;
  classType: string | null;
  gradeLevel: number | null;
  roomNumber: string | null;
  enrolledAt: string | null;
  droppedAt: string | null;
}

export interface EnrolledClassWithBlocks extends StudentEnrollmentClass {
  blocks: Array<{
    blockId: string;
    blockName: string;
    displayOrder: number;
    isEnrolled: boolean;
    overrideReason: string | null;
  }>;
}

export interface BlockOverride {
  studentId: string;
  blockId: string;
  isEnrolled: boolean;
  reason: string;
}

/**
 * Staff assignment for a class
 */
export interface ClassStaffAssignment {
  staffId: string;
  staffName: string;
  staffEmail: string;
  role: 'primary_teacher' | 'specialist' | 'supervisor' | 'aide';
}

// Schedule Grid Types (from OpenAPI generated schemas)
export type ScheduleMeetingPattern = components['schemas']['ScheduleMeetingPatternResponse'];
export type ScheduleEnrollmentClass = components['schemas']['ScheduleEnrollmentClassResponse'];
export type SyncEnrollmentsResult = components['schemas']['SyncEnrollmentsResponse'];

/**
 * Enrollment API endpoints
 */
export const enrollmentsApi = {
  /**
   * Get class roster (Phase 1 - without blocks)
   */
  getClassRoster: async (classId: string, params?: { search?: string; limit?: number; offset?: number }) => {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());

    const queryString = query.toString();
    return apiRequest<{
      success: boolean;
      data: ClassRosterStudent[];
    }>(`/api/classes/${classId}/roster${queryString ? '?' + queryString : ''}`);
  },

  /**
   * Get enrolled students with blocks (Phase 2 - for override management)
   */
  getEnrolledStudentsWithBlocks: async (classId: string) => {
    return apiRequest<{
      success: boolean;
      data: EnrolledStudentWithBlocks[];
    }>(`/api/classes/${classId}/roster-with-blocks`);
  },

  /**
   * Bulk enroll students into a class
   */
  bulkEnrollInClass: async (classId: string, studentIds: string[]) => {
    return apiRequest<{
      success: boolean;
      message: string;
      data: { enrolled: number; reactivated: number; skipped: number };
    }>(`/api/classes/${classId}/enrollments`, {
      method: 'POST',
      body: JSON.stringify({ studentIds, classId }),
    });
  },

  /**
   * Unenroll student from class
   */
  unenrollFromClass: async (classId: string, studentId: string) => {
    return apiRequest<{
      success: boolean;
      message: string;
    }>(`/api/classes/${classId}/enrollments/${studentId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get student enrollments (Phase 1 - without blocks)
   */
  getStudentEnrollments: async (studentId: string) => {
    return apiRequest<{
      success: boolean;
      data: StudentEnrollmentClass[];
    }>(`/api/students/${studentId}/enrollments`);
  },

  /**
   * Get student's enrolled classes with blocks (Phase 2)
   */
  getStudentEnrolledClassesWithBlocks: async (studentId: string) => {
    return apiRequest<{
      success: boolean;
      data: EnrolledClassWithBlocks[];
    }>(`/api/students/${studentId}/enrollments-with-blocks`);
  },

  /**
   * Bulk enroll student into classes
   */
  bulkEnrollStudent: async (studentId: string, classIds: string[]) => {
    return apiRequest<{
      success: boolean;
      message: string;
      data: { enrolled: number; reactivated: number; skipped: number };
    }>(`/api/students/${studentId}/enrollments`, {
      method: 'POST',
      body: JSON.stringify({ studentId, classIds }),
    });
  },

  /**
   * Single atomic batch endpoint for ALL overrides
   * Accepts overrides for multiple students in one transaction
   */
  batchBlockOverrides: async (overrides: BlockOverride[]) => {
    // Get first studentId for URL (batch accepts all students)
    const studentId = overrides[0]?.studentId;
    if (!studentId) {
      throw new Error('No overrides provided');
    }

    return apiRequest<{
      success: boolean;
      message: string;
      data: { count: number };
    }>(`/api/students/${studentId}/blocks/overrides/batch`, {
      method: 'POST',
      body: JSON.stringify({ overrides }),
    });
  },

  /**
   * Delete block override
   */
  deleteBlockOverride: async (studentId: string, blockId: string) => {
    return apiRequest<{
      success: boolean;
      message: string;
    }>(`/api/students/${studentId}/blocks/${blockId}/override`, {
      method: 'DELETE',
    });
  },

  // ============================================
  // Schedule Grid Endpoints
  // ============================================

  /**
   * Get student enrollments with full schedule data for grid visualization
   * Includes: teacher info, meeting patterns, block-level override status
   */
  getStudentEnrollmentsWithSchedule: async (studentId: string) => {
    return apiRequest<{
      success: boolean;
      data: ScheduleEnrollmentClass[];
    }>(`/api/students/${studentId}/enrollments-schedule`);
  },

  /**
   * Sync student enrollments - add new, drop removed
   * Compares selected class IDs with current enrollments
   */
  syncStudentEnrollments: async (studentId: string, classIds: string[]) => {
    return apiRequest<{
      success: boolean;
      message: string;
      data: SyncEnrollmentsResult;
    }>(`/api/students/${studentId}/enrollments/sync`, {
      method: 'PUT',
      body: JSON.stringify({ classIds }),
    });
  },

  /**
   * Get staff assignments for a class
   * Returns all staff (primary teacher, specialists, aides) assigned to the class
   */
  getClassStaff: async (classId: string) => {
    return apiRequest<{
      success: boolean;
      data: ClassStaffAssignment[];
    }>(`/api/staff-assignments/by-class/${classId}`);
  },
};
